import { ArrayBufferTarget, Muxer } from "mp4-muxer";
import { analyzeVideoFrame, buildVideoTrackConfig } from "./clipBitstream";

const { alawByteToPcm16, mulawByteToPcm16, resampleG711 } = require("./codec");
const { parseDgfFrameTime } = require("./playbackMedia");

const VIDEO_CODECS = { "video/h264": "H264", "video/h265": "H265", "video/hevc": "H265" };
// Same content types the player accepts (playbackMedia PLAYBACK_AUDIO_CODECS).
const AUDIO_DECODERS = {
  "audio/pcma": alawByteToPcm16,
  "audio/pcmu": mulawByteToPcm16,
  "audio/g711-alaw": alawByteToPcm16,
  "audio/g711-ulaw": mulawByteToPcm16,
};
// Audio dgf times further than this from the first video frame are on another clock.
const MAX_AUDIO_TIMESTAMP_OFFSET_MS = 5000;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_FRAME_MS = 33;
const AUDIO_SAMPLE_RATE = 48000;
const AUDIO_ENCODE_BLOCK_SAMPLES = AUDIO_SAMPLE_RATE;
// AAC plays everywhere; Opus-in-MP4 covers browsers without an AAC encoder
// (Firefox, Linux) and plays in Chrome / Firefox / VLC but not QuickTime.
const AUDIO_ENCODER_CONFIGS = [
  { muxerCodec: "aac", config: { codec: "mp4a.40.2", sampleRate: AUDIO_SAMPLE_RATE, numberOfChannels: 1, bitrate: 64000 } },
  { muxerCodec: "opus", config: { codec: "opus", sampleRate: AUDIO_SAMPLE_RATE, numberOfChannels: 1, bitrate: 64000 } },
];

function dgfDelta(later, earlier) {
  let delta = later - earlier;
  if (delta > MILLISECONDS_PER_DAY / 2) delta -= MILLISECONDS_PER_DAY;
  else if (delta < -MILLISECONDS_PER_DAY / 2) delta += MILLISECONDS_PER_DAY;
  return delta;
}

function mediaType(contentType) {
  return String(contentType || "").split(";")[0].trim().toLowerCase();
}

function toBytes(source) {
  if (!source) return null;
  if (source instanceof ArrayBuffer) return new Uint8Array(source.slice(0));
  return new Uint8Array(source.buffer, source.byteOffset, source.byteLength).slice();
}

async function pickAudioEncoder() {
  if (!globalThis.AudioEncoder || !globalThis.AudioData) return null;
  for (const candidate of AUDIO_ENCODER_CONFIGS) {
    try {
      const support = await globalThis.AudioEncoder.isConfigSupported(candidate.config);
      if (support?.supported === true) return candidate;
    } catch (_error) {
      // Try the next codec.
    }
  }
  return null;
}

/**
 * Encodes mono 48 kHz Float32 PCM with WebCodecs (AAC, else Opus).
 * Resolves to null when the browser has no usable audio encoder.
 * @returns {Promise<{muxerCodec: string, config: object, description: Uint8Array|null,
 *   chunks: {data: Uint8Array, timestampUs: number, durationUs: number}[]}|null>}
 */
export async function encodeAudioWithWebCodecs(pcm) {
  const candidate = await pickAudioEncoder();
  if (!candidate) return null;
  const chunks = [];
  let description = null;
  let encodeError = null;
  const encoder = new globalThis.AudioEncoder({
    output: (chunk, meta) => {
      const data = new Uint8Array(chunk.byteLength);
      chunk.copyTo(data);
      chunks.push({ data, timestampUs: chunk.timestamp, durationUs: chunk.duration ?? 0 });
      if (!description && meta?.decoderConfig?.description) {
        description = toBytes(meta.decoderConfig.description);
      }
    },
    error: (error) => {
      encodeError = error;
    },
  });
  encoder.configure(candidate.config);
  for (let offset = 0; offset < pcm.length; offset += AUDIO_ENCODE_BLOCK_SAMPLES) {
    const block = pcm.slice(offset, Math.min(pcm.length, offset + AUDIO_ENCODE_BLOCK_SAMPLES));
    const audioData = new globalThis.AudioData({
      format: "f32",
      sampleRate: AUDIO_SAMPLE_RATE,
      numberOfFrames: block.length,
      numberOfChannels: 1,
      timestamp: Math.round((offset * 1e6) / AUDIO_SAMPLE_RATE),
      data: block,
    });
    encoder.encode(audioData);
    audioData.close();
  }
  await encoder.flush();
  encoder.close();
  if (encodeError) throw encodeError;
  return { ...candidate, description, chunks };
}

/** Rebases encoder timestamps to 0 and keeps them strictly increasing. */
function normalizeAudioChunks(chunks) {
  if (!chunks.length) return [];
  const origin = chunks[0].timestampUs;
  let previous = -Infinity;
  return chunks.reduce((output, chunk) => {
    const timestampUs = Math.max(0, chunk.timestampUs - origin);
    if (timestampUs <= previous) return output;
    previous = timestampUs;
    output.push({ ...chunk, timestampUs });
    return output;
  }, []);
}

/**
 * Collects multipart playback parts (possibly across reconnects) and writes
 * them into a single MP4 (H.264/H.265 + AAC) when finished.
 */
export class PlaybackClipRecorder {
  constructor({ maxDurationMs = Infinity } = {}) {
    this.maxDurationMs = maxDurationMs;
    this.videoCodec = null;
    this.params = null;
    this.baseTimestamp = null;
    this.frames = [];
    this.audioParts = [];
    this.awaitKeyframe = true;
    this.otherContentTypes = new Set();
  }

  get recordedMs() {
    const last = this.frames[this.frames.length - 1];
    return last ? last.relMs : 0;
  }

  get hasVideo() {
    return this.frames.length > 0;
  }

  /** Call before feeding a new connection: the next frame must be a keyframe. */
  expectResume() {
    this.awaitKeyframe = true;
  }

  push(part) {
    const type = mediaType(part.contentType);
    if (VIDEO_CODECS[type]) this.pushVideo(part, VIDEO_CODECS[type]);
    else if (AUDIO_DECODERS[type]) this.pushAudio(part, AUDIO_DECODERS[type]);
    else this.otherContentTypes.add(type);
  }

  pushVideo(part, codec) {
    if (this.videoCodec && codec !== this.videoCodec) return;
    const frame = analyzeVideoFrame(codec, part.body);
    if (!frame.sample) return;

    if (!this.params && frame.params.sps.length && frame.params.pps.length) {
      this.params = frame.params;
      this.videoCodec = codec;
    }
    if (!this.params) return;
    if (this.awaitKeyframe && !frame.key) return;

    const timestamp = parseDgfFrameTime(part.headers?.["dgf-frametime"]);
    if (this.baseTimestamp === null) {
      this.baseTimestamp = timestamp ?? 0;
    }
    const last = this.frames[this.frames.length - 1];
    const relMs = timestamp === null
      ? (last ? last.relMs + DEFAULT_FRAME_MS : 0)
      : dgfDelta(timestamp, this.baseTimestamp);
    if (last && relMs <= last.relMs) return;
    if (relMs < 0 || relMs > this.maxDurationMs) return;

    this.awaitKeyframe = false;
    this.frames.push({ relMs, key: frame.key, data: frame.sample });
  }

  pushAudio(part, decode) {
    if (!part.body?.length) return;
    this.audioParts.push({
      timestamp: parseDgfFrameTime(part.headers?.["dgf-frametime"]),
      decode,
      data: part.body,
    });
  }

  frameDurationsMs() {
    return this.frames.map((frame, index) => {
      const next = this.frames[index + 1];
      if (next) return next.relMs - frame.relMs;
      const previous = this.frames[index - 1];
      return previous ? Math.min(frame.relMs - previous.relMs, 1000) : DEFAULT_FRAME_MS;
    });
  }

  /**
   * Places audio on the video timeline by dgf time. Without usable audio
   * timestamps (missing, or on another clock) the parts are laid end to end
   * from 0 instead, the same way the live player plays them.
   */
  buildPcm(totalMs) {
    const pcm = new Float32Array(Math.ceil((totalMs * AUDIO_SAMPLE_RATE) / 1000));
    const firstTimed = this.audioParts.find((part) => part.timestamp !== null);
    const firstOffsetMs = firstTimed ? dgfDelta(firstTimed.timestamp, this.baseTimestamp) : null;
    const useTimestamps = firstOffsetMs !== null && Math.abs(firstOffsetMs) <= MAX_AUDIO_TIMESTAMP_OFFSET_MS;
    let cursor = 0;
    let writtenSamples = 0;
    for (const part of this.audioParts) {
      const samples = resampleG711(part.data, part.decode, AUDIO_SAMPLE_RATE);
      const start = useTimestamps && part.timestamp !== null
        ? Math.round((dgfDelta(part.timestamp, this.baseTimestamp) * AUDIO_SAMPLE_RATE) / 1000)
        : cursor;
      cursor = start + samples.length;
      const from = Math.max(0, -start);
      const to = Math.min(samples.length, pcm.length - start);
      if (to <= from) continue;
      pcm.set(samples.subarray(from, to), start + from);
      writtenSamples += to - from;
    }
    this.audioStats = {
      parts: this.audioParts.length,
      timestamped: this.audioParts.filter((part) => part.timestamp !== null).length,
      firstOffsetMs,
      placement: useTimestamps ? "timestamp" : "sequential",
      writtenSeconds: Number((writtenSamples / AUDIO_SAMPLE_RATE).toFixed(2)),
    };
    return writtenSamples > 0 ? pcm : null;
  }

  /**
   * @param {object} options
   * @param {(pcm: Float32Array) => Promise<object|null>} [options.encodeAudio] see encodeAudioWithWebCodecs
   * @returns {Promise<{buffer: ArrayBuffer, hasAudio: boolean, audioCodec: string|null,
   *   audioDropped: boolean, durationMs: number}>}
   */
  async finalize({ encodeAudio = encodeAudioWithWebCodecs } = {}) {
    if (!this.frames.length) throw new Error("No video was received for the selected range.");
    const track = buildVideoTrackConfig(this.videoCodec, this.params);
    if (!track) throw new Error("The recording is missing video parameter sets.");

    const durations = this.frameDurationsMs();
    const totalMs = this.recordedMs + durations[durations.length - 1];

    const pcm = this.audioParts.length ? this.buildPcm(totalMs) : null;
    const audio = pcm ? await encodeAudio(pcm) : null;
    const audioChunks = audio ? normalizeAudioChunks(audio.chunks) : [];
    const hasAudio = audioChunks.length > 0;
    console.info("[ClipDownload] audio", {
      ...(this.audioStats || { parts: 0 }),
      otherContentTypes: [...this.otherContentTypes],
      encoder: audio?.muxerCodec ?? (pcm ? "unsupported" : "none"),
      encodedChunks: audioChunks.length,
    });

    const target = new ArrayBufferTarget();
    const muxer = new Muxer({
      target,
      fastStart: "in-memory",
      firstTimestampBehavior: "strict",
      video: { codec: track.codec, width: track.width, height: track.height },
      ...(hasAudio
        ? { audio: { codec: audio.muxerCodec, numberOfChannels: 1, sampleRate: AUDIO_SAMPLE_RATE } }
        : {}),
    });

    const audioMeta = hasAudio && audio.description
      ? { decoderConfig: { ...audio.config, description: audio.description } }
      : undefined;
    let videoIndex = 0;
    let audioIndex = 0;
    while (videoIndex < this.frames.length || audioIndex < audioChunks.length) {
      const videoUs = videoIndex < this.frames.length ? this.frames[videoIndex].relMs * 1000 : Infinity;
      const audioUs = audioIndex < audioChunks.length ? audioChunks[audioIndex].timestampUs : Infinity;
      if (videoUs <= audioUs) {
        const frame = this.frames[videoIndex];
        muxer.addVideoChunkRaw(
          frame.data,
          frame.key ? "key" : "delta",
          videoUs,
          durations[videoIndex] * 1000,
          videoIndex === 0 ? { decoderConfig: track.decoderConfig } : undefined,
        );
        videoIndex += 1;
      } else {
        const chunk = audioChunks[audioIndex];
        muxer.addAudioChunkRaw(
          chunk.data,
          "key",
          audioUs,
          chunk.durationUs,
          audioIndex === 0 ? audioMeta : undefined,
        );
        audioIndex += 1;
      }
    }
    muxer.finalize();

    return {
      buffer: target.buffer,
      hasAudio,
      audioCodec: hasAudio ? audio.muxerCodec : null,
      audioDropped: Boolean(pcm) && !hasAudio,
      durationMs: totalMs,
    };
  }
}
